import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Scale, ChevronUp, ChevronDown, Copy } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { http } from "@/lib/api";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, Field, SelectField } from "@/components/kit/fields";
import { SearchSelect, MultiSearchSelect } from "@/components/kit/SearchSelect";
import { userOptions } from "@/lib/users";
import { fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", stream_key: "", tracking_domain: "", status: "active", buyer_id: "", items: [], sticky_enabled: false, sticky_minutes: 1440 };

const STICKY_OPTS = [
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "360", label: "6 hours" },
  { value: "720", label: "12 hours" },
  { value: "1440", label: "24 hours" },
  { value: "4320", label: "3 days" },
  { value: "10080", label: "7 days" },
  { value: "43200", label: "30 days" },
];

// Split 100% equally across enabled items (largest-remainder → sum is exactly 100).
function equalize(items) {
  const enabled = items.filter((it) => it.enabled);
  const n = enabled.length;
  const next = items.map((it) => ({ ...it }));
  if (n === 0) { next.forEach((it) => (it.weight = 0)); return next; }
  const base = Math.floor(100 / n);
  let rem = 100 - base * n;
  let i = 0;
  next.forEach((it) => {
    if (it.enabled) { it.weight = base + (i < rem ? 1 : 0); i += 1; }
    else it.weight = 0;
  });
  return next;
}

const totalWeight = (items) => items.filter((it) => it.enabled).reduce((s, it) => s + (Number(it.weight) || 0), 0);

function domainPool(items) {
  const out = [];
  items.forEach((it) => {
    if (it.enabled && it.stream_active && it.domain && !out.includes(it.domain)) out.push(it.domain);
  });
  return out;
}

export function MultiLinkDrawer({ open, item, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [ownerConfirm, setOwnerConfirm] = useState(null); // pending owner id awaiting confirmation
  const isEdit = !!item?.id;

  const { data: ownersData } = useQuery({
    queryKey: ["multilink-owners"],
    queryFn: async () => (await http.get("/multilinks/allowed-owners")).data,
    enabled: open,
  });
  const owners = useMemo(() => ownersData?.items || [], [ownersData]);
  const ownerOpts = useMemo(() => userOptions(owners), [owners]);

  const { data: avail } = useQuery({
    queryKey: ["multilink-available-streams", form.buyer_id],
    queryFn: async () => (await http.get(`/multilinks/available-streams?owner_id=${form.buyer_id}`)).data,
    enabled: open && !!form.buyer_id,
  });
  const availStreams = useMemo(() => avail?.items || [], [avail]);

  // Load full detail (items with weights) when editing.
  const { data: detail } = useQuery({
    queryKey: ["multilink", item?.id],
    queryFn: async () => (await http.get(`/multilinks/${item.id}`)).data,
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && detail) {
      setForm({
        name: detail.name || "", stream_key: detail.stream_key || "",
        tracking_domain: detail.tracking_domain || "", status: detail.status || "active",
        buyer_id: detail.buyer_id || "",
        sticky_enabled: !!detail.sticky_enabled,
        sticky_minutes: detail.sticky_minutes || 1440,
        items: (detail.items || []).map((it) => ({
          stream_id: it.stream_id, name: it.name, offer_name: it.offer_name, geo: it.geo,
          payout: it.payout, payout_currency: it.payout_currency, domain: it.domain,
          stream_active: it.stream_active, weight: it.weight || 0, enabled: !!it.enabled,
        })),
      });
    } else if (!isEdit) {
      setForm(empty);
    }
  }, [open, isEdit, detail]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItems = (updater) => setForm((f) => ({ ...f, items: typeof updater === "function" ? updater(f.items) : updater }));

  // Auto-select the owner when only one is possible (e.g. a buyer can only pick themself).
  useEffect(() => {
    if (open && !isEdit && !form.buyer_id && owners.length === 1) set("buyer_id", owners[0].id);
  }, [open, isEdit, owners, form.buyer_id]); // eslint-disable-line

  const pickOwner = (uid) => {
    if (uid === form.buyer_id) return;
    if (form.items.length > 0) { setOwnerConfirm(uid); return; }
    set("buyer_id", uid);
  };
  const confirmOwnerChange = () => {
    setForm((f) => ({ ...f, buyer_id: ownerConfirm, items: [], tracking_domain: "" }));
    setOwnerConfirm(null);
  };

  const pool = useMemo(() => domainPool(form.items), [form.items]);
  const total = totalWeight(form.items);
  const weightOk = total === 100;

  // Keep chosen domain valid against the current pool.
  useEffect(() => {
    if (pool.length && !pool.includes(form.tracking_domain)) set("tracking_domain", pool[0]);
    if (!pool.length && form.tracking_domain) set("tracking_domain", "");
  }, [pool]); // eslint-disable-line

  // All available streams (newest first from the backend). Already-added ones stay in the list,
  // check-marked, so the dropdown doesn't jump while adding several streams in a row.
  const addable = useMemo(() => availStreams.map((s) => ({
    value: s.stream_id,
    label: `#${s.tenant_seq ?? "?"} — ${s.name}${s.offer_name && s.offer_name !== "—" ? ` — ${s.offer_name}` : ""} — ${s.stream_key}`,
    search: `#${s.tenant_seq ?? ""} ${s.tenant_seq ?? ""} ${s.name} ${s.stream_key} ${s.geo} ${s.offer_name} ${s.domain}`,
  })), [availStreams]);

  const addStream = (sid) => {
    const s = availStreams.find((x) => x.stream_id === sid);
    if (!s) return;
    setItems((items) => items.some((it) => it.stream_id === sid) ? items : [...items, {
      stream_id: s.stream_id, name: s.name, offer_name: s.offer_name, geo: s.geo,
      payout: s.payout, payout_currency: s.payout_currency, domain: s.domain,
      stream_active: s.stream_active, weight: 0, enabled: true,
    }]);
  };

  const removeStream = (sid) => setItems((items) => equalize(items.filter((it) => it.stream_id !== sid)));

  const toggleEnabled = (sid) => setItems((items) => {
    const it = items.find((x) => x.stream_id === sid);
    if (!it) return items;
    if (!it.enabled && !it.stream_active) { toast.error("Activate the Stream itself first."); return items; }
    const next = items.map((x) => (x.stream_id === sid ? { ...x, enabled: !x.enabled } : x));
    // Auto-redistribute only when DISABLING a stream.
    if (it.enabled) return equalize(next);
    return next;
  });

  const setWeight = (sid, v) => setItems((items) => items.map((x) => (x.stream_id === sid ? { ...x, weight: Math.max(0, Math.min(100, Number(v) || 0)) } : x)));

  const move = (idx, dir) => setItems((items) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return items;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const save = async () => {
    if (!form.buyer_id) return toast.error("Select the Buyer / Owner first");
    if (isEdit && !form.name.trim()) return toast.error("Enter a name");
    if (form.items.length === 0) return toast.error("Add at least one stream");
    if (isEdit && form.stream_key && !/^[A-Za-z0-9_-]{4,40}$/.test(form.stream_key.trim()))
      return toast.error("Stream key must be 4–40 chars: letters, numbers, - or _");
    if (!weightOk) return toast.error(`Total weight must be 100% (currently ${total}%)`);
    const body = {
      name: form.name.trim(), tracking_domain: form.tracking_domain, buyer_id: form.buyer_id,
      sticky_enabled: form.sticky_enabled, sticky_minutes: Number(form.sticky_minutes) || 0,
      items: form.items.map((it) => ({ stream_id: it.stream_id, weight: it.weight, enabled: it.enabled })),
    };
    if (isEdit) { body.stream_key = form.stream_key.trim(); body.status = form.status; }
    setSaving(true);
    try {
      const res = isEdit
        ? await http.put(`/multilinks/${item.id}`, body)
        : await http.post("/multilinks", body);
      qc.invalidateQueries({ queryKey: ["multilinks"] });
      qc.invalidateQueries({ queryKey: ["multilink", item?.id] });
      if (res.data?.warning) toast.warning(res.data.warning);
      else toast.success(isEdit ? "Multi Link updated" : "Multi Link created");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const linkPreview = form.tracking_domain
    ? `https://${form.tracking_domain}/api/r/${form.stream_key || "<auto>"}`
    : `/api/r/${form.stream_key || "<auto>"}`;

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={isEdit ? "Edit Multi Link" : "New Multi Link"}
      subtitle={isEdit ? form.name : "One tracking link that splits traffic across your streams by weight"} size="lg"
      onSave={save} saving={saving} saveLabel={isEdit ? "Save changes" : "Create Multi Link"} testId="multilink-drawer">
      <div className="space-y-5">
        <TextField label={isEdit ? "Name" : "Name (optional)"} value={form.name} onChange={(v) => set("name", v)} testId="multilink-name"
          placeholder={isEdit ? "e.g. Facebook Split" : "Leave empty → auto Multi Link #ID"} />

        <Field label="Buyer / Owner" hint={isEdit ? "Changing the owner clears the current streams — you'll need to re-pick from the new owner's streams." : "Pick who this Multi Link belongs to. Only that user's streams can be added."}>
          <SearchSelect value={form.buyer_id} onChange={pickOwner} options={ownerOpts}
            placeholder="Select owner..." searchPlaceholder="Search by ID, name or email..."
            testId="multilink-owner" widthClass="w-[420px]" disabled={ownerOpts.length === 0} />
        </Field>

        {!form.buyer_id && (
          <div className="rounded-lg border border-[#EAB308]/30 bg-[#EAB308]/[0.07] px-3 py-2.5 text-xs text-[#EAB308]" data-testid="multilink-owner-required">
            Select the Buyer / Owner first — streams and tracking domain unlock after that.
          </div>
        )}

        {isEdit && (
          <TextField label="Stream key" value={form.stream_key} onChange={(v) => set("stream_key", v)}
            testId="multilink-key-input" hint="4–40 chars, unique across the whole system." />
        )}

        <Field label="Tracking domain" hint="Only domains used by the streams in this Multi Link are available.">
          {!form.buyer_id ? (
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5 text-xs text-muted-foreground" data-testid="multilink-domain-locked">
              Locked until an owner is selected.
            </div>
          ) : pool.length > 0 ? (
            <SelectField value={pool.includes(form.tracking_domain) ? form.tracking_domain : pool[0]}
              onChange={(v) => set("tracking_domain", v)} options={pool} testId="multilink-domain" />
          ) : (
            <div className="rounded-lg border border-[#EAB308]/30 bg-[#EAB308]/[0.07] px-3 py-2.5 text-xs text-[#EAB308]" data-testid="multilink-no-domain">
              Add at least one active stream to unlock a tracking domain.
            </div>
          )}
        </Field>

        {isEdit && (
          <Field label="Status">
            <SelectField value={form.status} onChange={(v) => set("status", v)}
              options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Disabled" }]} testId="multilink-status" />
          </Field>
        )}

        <div className="space-y-3 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Sticky Stream</p>
              <p className="text-[11px] text-muted-foreground">Pin each visitor to the stream they first landed on for the whole period (Keitaro-style). Weight changes never break existing pins.</p>
            </div>
            <Switch checked={form.sticky_enabled} onCheckedChange={(v) => set("sticky_enabled", v)} data-testid="multilink-sticky-toggle" />
          </div>
          {form.sticky_enabled && (
            <Field label="Sticky Period" hint="After it expires the visitor is re-distributed by weights and pinned again.">
              <SelectField value={String(form.sticky_minutes)} onChange={(v) => set("sticky_minutes", Number(v))}
                options={STICKY_OPTS} testId="multilink-sticky-period" />
            </Field>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">Streams &amp; weights</label>
            <button type="button" onClick={() => setItems((items) => equalize(items))} data-testid="multilink-equalize"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.12] px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-white/[0.06]">
              <Scale className="h-3.5 w-3.5" /> Equalize
            </button>
          </div>

          <div className="space-y-2" data-testid="multilink-items">
            {form.items.length === 0 && (
              <div className="rounded-lg border border-dashed border-white/[0.12] py-6 text-center text-xs text-muted-foreground">
                {form.buyer_id ? "No streams added yet. Pick the owner's streams below." : "Select the owner first to see their streams."}
              </div>
            )}
            {form.items.map((it, idx) => (
              <div key={it.stream_id} data-testid={`multilink-item-${it.stream_id}`}
                className={cn("flex items-center gap-3 rounded-lg border px-3 py-2.5",
                  it.enabled ? "border-white/[0.1] bg-[#0A0A0A]" : "border-white/[0.06] bg-[#0A0A0A]/40 opacity-70")}>
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="text-muted-foreground disabled:opacity-30 hover:text-foreground"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === form.items.length - 1} className="text-muted-foreground disabled:opacity-30 hover:text-foreground"><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 truncate text-sm text-foreground">
                    {it.name || "Stream"}
                    {it.geo && <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">{it.geo}</span>}
                    {!it.stream_active && <span className="rounded bg-[#EF4444]/15 px-1.5 py-0.5 text-[10px] text-[#EF4444]">Stream disabled</span>}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">{it.offer_name} · {it.payout != null ? fmtMoney(it.payout) : "—"} · {it.domain}</div>
                </div>
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="100" value={it.weight} disabled={!it.enabled}
                    onChange={(e) => setWeight(it.stream_id, e.target.value)} data-testid={`multilink-weight-${it.stream_id}`}
                    className="h-8 w-16 rounded-md border border-white/[0.12] bg-[#141417] px-2 text-right text-sm text-foreground disabled:opacity-50" />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
                <label className="flex cursor-pointer items-center" title={it.enabled ? "Active" : "Disabled"}>
                  <input type="checkbox" checked={it.enabled} onChange={() => toggleEnabled(it.stream_id)}
                    data-testid={`multilink-toggle-${it.stream_id}`} className="h-4 w-4 accent-[#22C55E]" />
                </label>
                <button type="button" onClick={() => removeStream(it.stream_id)} data-testid={`multilink-remove-${it.stream_id}`}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[#EF4444]/10 hover:text-[#EF4444]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className={cn("flex items-center justify-between rounded-lg border px-3 py-2 text-xs font-medium",
            weightOk ? "border-[#22C55E]/30 bg-[#22C55E]/[0.07] text-[#22C55E]" : "border-[#EF4444]/30 bg-[#EF4444]/[0.07] text-[#EF4444]")}
            data-testid="multilink-total-weight">
            <span>Total weight</span>
            <span>{total} / 100%{weightOk ? "" : (total > 100 ? " · over" : " · under")}</span>
          </div>

          {form.buyer_id && addable.length > 0 && (
            <div className="pt-1">
              <MultiSearchSelect values={form.items.map((it) => it.stream_id)} onAdd={addStream} options={addable}
                placeholder="+ Add existing streams" searchPlaceholder="Search by ID, name, key, GEO offer..." testId="multilink-add-stream" widthClass="w-[420px]" />
            </div>
          )}
        </div>

        <Field label="Tracking link">
          <div className="flex items-center gap-2 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-2.5">
            <code className="flex-1 truncate font-mono text-xs text-muted-foreground" data-testid="multilink-tracking-link">{linkPreview}</code>
            {isEdit && <button onClick={() => copyText(linkPreview)} className="text-muted-foreground hover:text-foreground"><Copy className="h-4 w-4" /></button>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground/70">Each click is routed to one stream by weight; the click is recorded for both the Multi Link and that stream.</p>
        </Field>
      </div>

      <AlertDialog open={!!ownerConfirm} onOpenChange={(o) => !o && setOwnerConfirm(null)}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="multilink-owner-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change owner?</AlertDialogTitle>
            <AlertDialogDescription>
              A Multi Link can only contain streams of one owner. Changing the owner will
              <span className="font-medium text-foreground"> remove all currently selected streams</span> — you&apos;ll pick streams of the new owner from scratch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="multilink-owner-change-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="multilink-owner-change-confirm" onClick={confirmOwnerChange} className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Change owner &amp; clear streams</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormDrawer>
  );
}
